<script setup lang="ts">
import { message } from 'ant-design-vue'
import { ref } from 'vue'
import { useI18n } from '../composables/useI18n'
import AvatarCropModal from './AvatarCropModal.vue'
import en from './locales/en'
import zh from './locales/zh'

const { t } = useI18n({ zh, en })

const props = withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false })
const emit = defineEmits<{ select: [file: File] }>()

const cropOpen = ref(false)
const cropSrc = ref('')
// Keep in sync with the hint shown in the account avatar panel (5MB)
const MAX_SIZE = 5 * 1024 * 1024

function releaseSrc() {
  if (cropSrc.value) {
    URL.revokeObjectURL(cropSrc.value)
    cropSrc.value = ''
  }
}

function beforeUpload(file: File) {
  if (!file.type.startsWith('image/')) {
    message.warning(t('base.avatarCrop.invalidImage'))
    return false
  }
  if (file.size > MAX_SIZE) {
    message.warning(t('base.avatarCrop.tooLarge'))
    return false
  }
  releaseSrc()
  cropSrc.value = URL.createObjectURL(file)
  cropOpen.value = true
  return false
}

function onConfirm(file: File) {
  cropOpen.value = false
  releaseSrc()
  emit('select', file)
}

function onCancel() {
  releaseSrc()
  cropOpen.value = false
}
</script>

<template>
  <a-upload
    :before-upload="beforeUpload"
    :show-upload-list="false"
    :disabled="props.disabled"
    accept="image/*"
  >
    <slot />
  </a-upload>
  <AvatarCropModal v-model:open="cropOpen" :src="cropSrc" @confirm="onConfirm" @cancel="onCancel" />
</template>
